// ReadingsFilters.tsx
import { useReadingStatus } from "@features/mx/routeManagement/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { UseFormReturn } from "react-hook-form";

export interface ReadingsFilters {
  fromDate: string;
  toDate: string;
  status: string;
}

interface ReadingsFiltersProps {
  filters: ReadingsFilters;
  onFiltersChange: (filters: ReadingsFilters) => void;
  onApplyFilters: (filters: ReadingsFilters) => void;
  onClearFilters: () => void;
}

export const ReadingsFilters = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
}: ReadingsFiltersProps) => {
  const FIELD_CLASSES = {
    container: "w-full mb-4",
    label: "block text-sm font-semibold text-gray-700 mb-2",
    input:
      "w-full h-11 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50",
    select:
      "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50",
    error: "text-red-600 text-sm mt-1",
    helperText: "text-xs text-gray-500 mt-1",
  } as const;
 const { data: readingStatusData } = useReadingStatus();
    const readingStatusOptions =
    readingStatusData?.result?.map((readingStatus) => ({
      value: readingStatus.key,
      label: readingStatus.value,
    })) || [];
  const createFormFields = (): FormField[] => [
    {
      name: "fromDate",
      label: "From Date",
      type: "datepicker",
      required: false,
      placeholder: "Select start date",
      classes: FIELD_CLASSES,
      helperText: "Start date for reading range (dd-mm-yyyy)",
    },
    {
      name: "toDate",
      label: "To Date",
      type: "datepicker",
      required: false,
      placeholder: "Select end date",
      classes: FIELD_CLASSES,
      helperText: "End date for reading range (dd-mm-yyyy)",
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      required: false,
      placeholder: "All Statuses",
      options: readingStatusOptions,
      classes: FIELD_CLASSES,
      helperText: "Filter by reading validation status",
    },
  ];

  const form: UseFormReturn<ReadingsFilters> =
    FormService.createForm<ReadingsFilters>({
      fields: createFormFields(),
      defaultValues: filters,
    });

  const handleSubmit = (data: ReadingsFilters) => {
    onApplyFilters(data);
  };

  const handleReset = () => {
    const defaultFilters: ReadingsFilters = {
      fromDate: "",
      toDate: "",
      status: "all",
    };

    form.reset(defaultFilters);
    onClearFilters();
  };

  const renderCustomFooter = () => (
    <div className="flex justify-between items-center pt-6 border-t border-gray-200">
      <button
        type="button"
        onClick={handleReset}
        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
        Clear Filters
      </button>

      <button
        type="submit"
        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
      >
        Apply Filters
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      <DynamicForm
        fields={createFormFields()}
        form={form}
        onSubmit={handleSubmit}
        onReset={handleReset}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          layout: "vertical",
          spacing: "medium",
        }}
        gridColumns={3}
        className="space-y-6"
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
};